import type { Delta } from 'quill/core';
import React from 'react';
import CardBanner from '../components/CardBanner';
import CardHeader from '../components/CardHeader';
import DeltaPreview from '../components/DeltaPreview';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import NestableLink from '../utils/NestableLink';
import renderHighlight from '../utils/search/renderHighlight';
import topicPath from '../utils/url/topicPath';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import * as styles from './TopicCardContent.module.scss';
import { TopicEntityIcon } from './TopicIcon';
import TopicNameWithIcon from './TopicNameWithIcon';
import TopicPills from './TopicPills';

const MAX_TOPIC_NUMBER = 5;

const TOPIC_FRAGMENT = gql(`
  fragment TopicCardContent_topic on TopicFields {
    id
    description
    ...topicPath_topic
    ...TopicNameWithIcon_topic
    ancestors {
      ...TopicBreadcrumbs_topics
    }

    ... on Topic {
      banner {
        ...CardBanner_banner
      }
    }
  }
`);

const ANCESTORS_FRAGMENT = gql(`
  fragment TopicCardContent_ancestors on AncestorSearchResult {
    id
    name
  }
`);

interface TopicCardContentProps {
  childTopics?: React.ComponentProps<typeof TopicPills>['topics'];
  description?: Delta | null;
  name?: string | null;
  numLines?: number;
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  ancestors?: DocumentType<typeof ANCESTORS_FRAGMENT>[];
}

const TopicCardContent = ({
  childTopics = [],
  description,
  name,
  numLines = 4,
  topic,
  ancestors = [],
}: TopicCardContentProps) => {
  const actualContent = description || topic.description;

  return (
    <>
      <NestableLink className={styles.header} to={topicPath(topic)}>
        {'banner' in topic && topic.banner && (
          <CardBanner banner={topic.banner} height={74} />
        )}
        <div className={styles.basic}>
          <CardHeader
            icon={<TopicEntityIcon />}
            aboveTitle={
              topic.ancestors &&
              topic.ancestors.length > 0 && (
                <TopicBreadcrumbs
                  topics={topic.ancestors}
                  names={topic.ancestors.map((ancestorTopic) => {
                    const ancestorName = ancestors.find(
                      (ancestor) => ancestor.id === ancestorTopic.id,
                    )?.name;

                    return ancestorName
                      ? renderHighlight(ancestorName)
                      : undefined;
                  })}
                  link
                  size="small"
                />
              )
            }
            title={
              <TopicNameWithIcon
                highlighted={!!name}
                iconSize="medium"
                name={name}
                topic={topic}
              />
            }
          />
          {!!actualContent && (
            <div className={styles.description}>
              <DeltaPreview
                typography="searchResult"
                content={actualContent}
                maxLines={numLines}
              />
            </div>
          )}
        </div>
      </NestableLink>
      {childTopics && childTopics.length > 0 && (
        <div className={styles.subTopics}>
          <TopicPills
            limit={MAX_TOPIC_NUMBER}
            moreLink={topicPath(topic, 'subtopics')}
            topics={childTopics}
            size="small"
          />
        </div>
      )}
    </>
  );
};

export default TopicCardContent;
